import {useGetEvents} from '@/features/agenda/queries/useGetEvents'
import {useGetPatient} from '@/features/patients/services/useGetPatient'
import {ProjectWarningMissingEvaluationEventCompleted} from '@/features/projects/components/project-warning-missing-banner/project-warning-missing-evaluation-event-completed/ProjectWarningMissingEvaluationEventCompleted'
import {ProjectWarningMissingEventCompleted} from '@/features/projects/components/project-warning-missing-banner/project-warning-missing-event-completed/ProjectWarningMissingEventCompleted'
import {ProjectWarningMissingOpeningEvent} from '@/features/projects/components/project-warning-missing-banner/project-warning-missing-opening-event/ProjectWarningMissingOpeningEvent'
import {ProjectWarningMissingOpeningRelation} from '@/features/projects/components/project-warning-missing-banner/project-warning-missing-opening-relation/ProjectWarningMissingOpeningRelation'
import {ProjectWarningMissingTeamReport} from '@/features/projects/components/project-warning-missing-banner/project-warning-missing-team-report/ProjectWarningMissingTeamReport'
import {ProjectExtended} from '@/features/projects/types'
import {ProjectWarningMissingRenewalReport} from '../project-warning-missing-renewal-report/ProjectWarningMissingRenewalReport'
import {ProjectWarningMissingAssessmentReport} from '../project-warning-missing-assessment-report/ProjectWarningMissingAssessmentReport'
import dayjs from 'dayjs'
import {ProjectWarningMissingCloseReport} from '../project-warning-missing-close-report/ProjectWarningMissingCloseReport'
import {ProjectWarningMissingEvaluationEvent} from '../project-warning-missing-evaluation-event/ProjectWarningMissingEvaluationEvent'

/**
 * Displays warning banners for missing project actions based on project state
 *
 * This component analyzes a project's completion status and displays appropriate
 * warning banners to guide users on what actions need to be completed. The banners
 * follow a hierarchical priority system:
 *
 * 1. Renewal report missing (highest priority — renewed project without report)
 * 2. Close report missing (expired project without renewal)
 * 3. Initial evaluation event not completed (present but provisional/scheduled)
 * 4. Initial evaluation assessment report missing (event completed, report absent)
 * 5–9. Only when initial evaluation event is absent and project is not closed:
 *   5. Evaluation event missing (only for projects with projectRefId)
 *   6. Opening event missing
 *   7. Opening event not completed (provisional/scheduled)
 *   8. Opening report missing (opening event present, report absent)
 *   9. Team report missing (lowest priority — requires opening report present)
 *
 * @param project - The extended project object containing completion flags and metadata
 * @returns JSX.Element | null - The appropriate warning banner component or null if no warnings
 */
export const ProjectWarningMissingActionBanner = ({project}: {project: ProjectExtended}) => {
    const {data: patient, isLoading: isPatientPending} = useGetPatient({
        patientId: project.patientId,
        responseType: 'extended'
    })

    const {data: events, isLoading: isEventsPending} = useGetEvents({
        params: {
            projectId: project.id
        },
        options: {
            enabled:
                !project.isOpeningEventPresent ||
                !project.isOpeningEventCompleted ||
                !project.isOpeningReportPresent ||
                !project.isTeamReportPresent
        }
    })

    /**
     * Finds the initial evaluation event from the project's events
     * Initial evaluation events have access type 'initial_project_evaluation'
     */
    const initialEvaluation = events?.find(event => {
        const accessType = event.eventConsultants?.[0]?.accessType?.accessType
        return (
            accessType == 'initial_project_evaluation' ||
            accessType == 'observation_and_evaluation_sir' ||
            accessType == 'observation_and_evaluation_sir_tr' ||
            accessType == 'observation_and_evaluation_sir_mrp'
        )
    })
    const isInitialEvaluationEventPresent = !!initialEvaluation
    const isInitialEvaluationCompleted = initialEvaluation?.status == 'completed'

    /**
     * Finds the opening event from the project's events
     * Opening events have access type 'project_opening_processing' or 'cycle_opening_processing'
     */
    const openingEvent = events?.find(
        event =>
            event.eventConsultants?.[0]?.accessType?.accessType == 'project_opening_processing' ||
            event.eventConsultants?.[0]?.accessType?.accessType == 'cycle_opening_processing'
    )

    /**
     * Determines which warning banner to display based on project completion status
     *
     * Priority order:
     * 1. Renewal report missing (highest priority)
     * 2. Close report missing
     * 3. Initial evaluation event not completed (provisional/scheduled)
     * 4. Initial evaluation assessment report missing
     * 5. Evaluation event missing (projects with projectRefId only)
     * 6. Opening event missing
     * 7. Opening event not completed (provisional/scheduled)
     * 8. Opening report missing (requires patient and project type)
     * 9. Team report missing (requires patient, project type, and opening report present)
     *
     * @returns JSX.Element | null - The appropriate warning banner or null
     */
    const renderMissingBanner = () => {
        // const isEvolvedProject =
        //     !!project.renewedProjectId || !!project.variedProjectId || !!project.transformedProjectId

        const isMissingCloseReport =
            !project.renewedAt &&
            patient &&
            project.expirationDate &&
            dayjs(project.expirationDate).endOf('day') < dayjs() &&
            project.type

        if (
            isPatientPending ||
            isEventsPending
            // ||
            // (project.status != 'opening' &&
            //     !!project.sirId &&
            //     isEvolvedProject &&
            //     (project.isRenewContinuousProjectReportPresent || !isMissingCloseReport)
            // )
        )
            return null

        if (!!project.renewedProjectId && patient && !project.isRenewContinuousProjectReportPresent)
            return (
                <ProjectWarningMissingRenewalReport
                    projectId={project.id}
                    patient={patient}
                    expirationDate={project.expirationDate ? new Date(project.expirationDate) : new Date()}
                />
            )

        if (isMissingCloseReport)
            return (
                <ProjectWarningMissingCloseReport
                    projectId={project.id}
                    patient={patient}
                    projectType={project.type!} // field ensured in isMissingCloseReport
                    expirationDate={new Date(project.expirationDate!)} // field ensured in isMissingCloseReport
                />
            )

        if (
            isInitialEvaluationEventPresent &&
            !isInitialEvaluationCompleted &&
            initialEvaluation?.id &&
            (initialEvaluation?.status == 'provisional' || initialEvaluation?.status == 'scheduled')
        )
            return <ProjectWarningMissingEvaluationEventCompleted eventId={initialEvaluation.id} />

        if (isInitialEvaluationEventPresent && patient && !project.isInitialProjectAssessmentReportPresent)
            return (
                <ProjectWarningMissingAssessmentReport
                    projectId={project.id}
                    patient={patient}
                    date={initialEvaluation.date ? new Date(initialEvaluation.date) : new Date()}
                />
            )

        if (!isInitialEvaluationEventPresent && project.status != 'closed') {
            if (project.projectRefId) {
                return <ProjectWarningMissingEvaluationEvent />
            }

            if (!project.isOpeningEventPresent) return <ProjectWarningMissingOpeningEvent />

            if (
                !project.isOpeningEventCompleted &&
                openingEvent?.id &&
                (openingEvent.status == 'provisional' || openingEvent.status == 'scheduled')
            )
                return <ProjectWarningMissingEventCompleted eventId={openingEvent.id} />

            if (!project.isOpeningReportPresent && patient && project.type && project.isOpeningEventPresent)
                return (
                    <ProjectWarningMissingOpeningRelation
                        projectId={project.id}
                        projectType={project.type}
                        patient={patient}
                    />
                )

            if (!project.isTeamReportPresent && patient && project.type && project.isOpeningReportPresent)
                return <ProjectWarningMissingTeamReport projectId={project.id} patient={patient} />
        }

        return null
    }

    return renderMissingBanner()
}
